import { IColumn, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPLink, IPScrollablePane, IPSpinner, IPText } from 'baseComponents';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import {
  getFontIconColorPrimary,
  globalStackItemStylesPaddingSceneScroll,
  globalStackStylesHeight100,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import Task from 'models/tasks/task';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetTaskInstancesByCount } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import SingleTask from './SingleTask';
import './TaskInstanceTabRowStyle.css';

interface ITaskInstanceTabProps {
  taskId: number;
  activeTaskId: number | undefined;
  windowLevel?: number;
  navigateExternal?: boolean;
  //a change reloads the instances: the host knows they changed on the server without a save of its own
  reloadToken?: number;
}

const TaskInstanceTab = (props: ITaskInstanceTabProps) => {
  const { t } = useTranslation(['translation', 'task']);
  const appContext = useContext(AppContext);
  const [instances, setInstances] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTask, setShowTask] = useState<ITaskStartDateTimeProps | undefined>(undefined);
  const theme = getTheme();
  const isManager = useMemo(() => hasUserFeatureGenericManager(appContext), [appContext]);

  interface ITaskStartDateTimeProps {
    startDateTime: Date;
    taskId: number;
    task: Task;
  }

  const TaskStartDateTime = (dateTimeProps: ITaskStartDateTimeProps) => {
    return (
      <IPStack horizontal>
        {dateTimeProps.taskId === props.activeTaskId && <span className="play-button"></span>}
        <IPPersona
          imageUrl={appContext.globalDataCache.users.getPhotoURLObj(dateTimeProps.task.userId)}
          size={IPPersonaSize.size24}
          text={dateTimeProps.task.user?.name}
          hidePersonaDetails={true}
        />
        <IPStack.Item styles={{ root: { paddingLeft: 5 } }}>
          <IPText styles={dateTimeProps.taskId === props.activeTaskId ? globalTextStylesBold : undefined}>
            <IPLink
              disabled={dateTimeProps.taskId === props.activeTaskId || !isManager}
              onClick={() => {
                setShowTask(dateTimeProps);
              }}
            >
              {toLocaleDateTimeMedium(dateTimeProps.startDateTime)}
            </IPLink>
          </IPText>
        </IPStack.Item>
        {dateTimeProps.task.eventId && (
          <IPStack.Item styles={{ root: { paddingLeft: 5 } }}>
            <IPFontIcon iconName="Calendar" style={{ color: getFontIconColorPrimary(appContext.useDarkMode, theme) }} />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.taskId, props.reloadToken]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasks = await apiGetTaskInstancesByCount(props.taskId, 365, accessToken, appContext.globalDataCache);
      setInstances(tasks);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'date',
      name: t('task:Start.Label'),
      isResizable: true,
      minWidth: 150,
      maxWidth: 250,
      onRender: (item?: Task, index?: number, column?: IColumn) => {
        if (!item) return;

        return <TaskStartDateTime startDateTime={item.startDateTime} taskId={item.taskId} task={item} />;
      },
    },
    {
      key: 'state',
      name: t('task:Status.Label'),
      isResizable: true,
      minWidth: 100,
      maxWidth: 250,
      onRender: (item?: Task, index?: number, column?: IColumn) => {
        if (!item) return;

        const state = item.taskStates?.find((s) => s.taskStateId === item.taskStateId);
        if (!state) return;

        return (
          <IPText styles={item.taskId === props.activeTaskId ? globalTextStylesBold : undefined}>{state.state}</IPText>
        );
      },
    },
    {
      key: 'completed',
      name: t('task:Completed.Label'),
      isResizable: true,
      isCollapsible: true,
      minWidth: 120,
      maxWidth: 250,
      onRender: (item?: Task, index?: number, column?: IColumn) => {
        if (!item) return;

        return (
          <IPText styles={item.taskId === props.activeTaskId ? globalTextStylesBold : undefined}>
            {toLocaleDateTimeMedium(item.completed)}
          </IPText>
        );
      },
    },
    {
      key: 'score',
      name: t('task:Score.Label'),
      isResizable: true,
      isCollapsible: true,
      minWidth: 50,
      maxWidth: 150,
      onRender: (item?: Task, index?: number, column?: IColumn) => {
        if (!item) return;

        return (
          <IPText styles={item.taskId === props.activeTaskId ? globalTextStylesBold : undefined}>
            {item.getScore(appContext.globalDataCache).toString()}
          </IPText>
        );
      },
    },
  ];

  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) await loadData();
  };

  const removeTask = async (task: Task) => {
    await loadData();
  };

  const updateTask = async (task: Task) => {};

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  } else if (!props.taskId) {
    return <p />;
  }

  return (
    <Fragment>
      <IPStack verticalFill styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
        <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPDetailsList
              items={instances}
              columns={columns}
              compact
              selectionMode={IPSelectionMode.none}
              layoutMode={IPDetailsListLayoutMode.justified}
              onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            />
          </IPScrollablePane>
        </IPStack.Item>
      </IPStack>
      {showTask && (
        <SingleTask
          task={showTask.task}
          isOpen={showTask !== undefined}
          close={() => {
            setShowTask(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
          windowLevel={(props.windowLevel ?? 0) + 1}
          navigateExternal={props.navigateExternal}
        />
      )}
    </Fragment>
  );
};

export default TaskInstanceTab;
